import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetLastMonthRevenueInsightsUseCase } from "../../domain/use-cases/GetLastMonthRevenueInsightsUseCase";
import type { LastMonthRevenueInsightItem } from "../../domain/entities/LastMonthRevenueInsightsEntity";

const repository = new ClientOverviewRepositoryImpl();
const useCase = new GetLastMonthRevenueInsightsUseCase(repository);

export interface LastMonthRevenueInsightsData {
  insights: LastMonthRevenueInsightItem[];
  noInsights: boolean;
  monthLabel: string;
  message: string | null;
  loading: boolean;
  error: string | null;
}

export interface UseLastMonthRevenueInsightsOptions {
  /** When false, the insights API is not called. Set to true only after last-month revenue has loaded. */
  enabled?: boolean;
}

export function useLastMonthRevenueInsights(
  options?: UseLastMonthRevenueInsightsOptions,
): LastMonthRevenueInsightsData {
  const enabled = options?.enabled ?? true;

  const [insights, setInsights] = useState<LastMonthRevenueInsightItem[]>([]);
  const [noInsights, setNoInsights] = useState(false);
  const [monthLabel, setMonthLabel] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const entity = await useCase.execute();
      setInsights(entity.insights);
      setNoInsights(entity.noInsights);
      setMonthLabel(entity.monthLabel);
      setMessage(entity.message);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load last month revenue insights",
      );
      setInsights([]);
      setNoInsights(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { insights, noInsights, monthLabel, message, loading, error };
}
